import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { CreditCard, Droplet, Trash2 } from "react-feather";
import CollapsibleCard, { PANEL_ACCENT } from "../../sales/components/CollapsibleCard";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import {
  useGetFuelCardsQuery,
  useGetFuelCardVehiclesQuery,
  useGetFuelLookupsQuery,
  useLinkFuelCardVehicleMutation,
  useUnlinkFuelCardVehicleMutation,
  useUpdateFuelVehicleProfileMutation,
} from "../fuelApi";
import { formatDate, money } from "../fuelFormat";

// ---- Fuel type + tank capacity ----
const ProfilePanel = ({ vehicle, canManage }) => {
  const { data: lookups } = useGetFuelLookupsQuery();
  const [updateProfile, { isLoading: isSaving }] = useUpdateFuelVehicleProfileMutation();
  const [fuelType, setFuelType] = useState("");
  const [tankCapacity, setTankCapacity] = useState("");

  useEffect(() => {
    setFuelType(vehicle.fuelType || "");
    setTankCapacity(vehicle.tankCapacityLitres ?? "");
  }, [vehicle.vehicleId, vehicle.fuelType, vehicle.tankCapacityLitres]);

  const handleSave = async () => {
    if (tankCapacity !== "" && Number(tankCapacity) <= 0) {
      toast.error("Tank capacity must be more than zero");
      return;
    }
    try {
      await updateProfile({
        vehicleId: vehicle.vehicleId,
        fuelType: fuelType || null,
        tankCapacityLitres: tankCapacity === "" ? null : Number(tankCapacity),
      }).unwrap();
      toast.success("Fuel profile saved");
    } catch (error) {
      toast.error(error?.message || "Could not save the fuel profile");
    }
  };

  return (
    <CollapsibleCard title="Fuel profile" icon={Droplet} accent={PANEL_ACCENT.services} defaultOpen>
      <div className="row g-3 align-items-end">
        <div className="col-md-4">
          <label className="form-label">Fuel type</label>
          <select className="form-control" value={fuelType} disabled={!canManage} onChange={(e) => setFuelType(e.target.value)}>
            <option value="">Not set</option>
            {(lookups?.fuelTypes || []).map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label">Tank capacity (litres)</label>
          <input
            type="number"
            min="0"
            step="any"
            className="form-control"
            placeholder="Not set"
            value={tankCapacity}
            disabled={!canManage}
            onChange={(e) => setTankCapacity(e.target.value)}
          />
        </div>
        {canManage && (
          <div className="col-md-4">
            <button type="button" className="btn btn-primary" disabled={isSaving} onClick={handleSave}>
              {isSaving ? "Saving…" : "Save profile"}
            </button>
          </div>
        )}
      </div>
      <small className="text-muted d-block mt-2">
        Used to check each purchase: a different fuel type, or more litres than the tank holds, is saved and sent for
        review. Leave blank to skip those checks for this vehicle.
      </small>
    </CollapsibleCard>
  );
};

// ---- Cards this vehicle may use ----
const CardsPanel = ({ vehicle, canManage }) => {
  const { data: links = [], isLoading } = useGetFuelCardVehiclesQuery({ vehicleId: vehicle.vehicleId });
  // Same branch only - the server refuses a card from another branch.
  const { data: cards = [] } = useGetFuelCardsQuery({ branchId: vehicle.branchId }, { skip: !canManage });
  const [linkCard, { isLoading: isLinking }] = useLinkFuelCardVehicleMutation();
  const [unlinkCard] = useUnlinkFuelCardVehicleMutation();
  const [fuelCardId, setFuelCardId] = useState("");
  const [asDefault, setAsDefault] = useState(false);

  const linkedIds = useMemo(() => new Set(links.map((l) => l.fuelCardId)), [links]);
  const candidates = cards.filter((c) => !linkedIds.has(c.fuelCardId));

  // First card for a vehicle is almost always meant to be its default.
  useEffect(() => {
    setAsDefault(links.length === 0);
  }, [links.length]);

  const handleAdd = async () => {
    if (!fuelCardId) return;
    try {
      await linkCard({ fuelCardId: Number(fuelCardId), vehicleId: vehicle.vehicleId, isDefault: asDefault }).unwrap();
      toast.success("Card assigned to the vehicle");
      setFuelCardId("");
    } catch (error) {
      toast.error(error?.message || "Could not assign the card");
    }
  };

  const handleSetDefault = async (link) => {
    try {
      await linkCard({ fuelCardId: link.fuelCardId, vehicleId: vehicle.vehicleId, isDefault: !link.isDefault }).unwrap();
      toast.success(link.isDefault ? "Default cleared" : `${link.cardNumber} is now the default card`);
    } catch (error) {
      toast.error(error?.message || "Could not change the default card");
    }
  };

  const handleRemove = async (link) => {
    const confirmed = await confirmDelete({
      title: `Remove card ${link.cardNumber} from ${vehicle.regNo}?`,
      text: "Purchases already recorded are not affected.",
    });
    if (!confirmed) return;
    try {
      await unlinkCard({ fuelCardId: link.fuelCardId, vehicleId: vehicle.vehicleId }).unwrap();
      toast.success("Card removed from the vehicle");
    } catch (error) {
      toast.error(error?.message || "Could not remove the card");
    }
  };

  return (
    <CollapsibleCard
      title="Fuel cards for this vehicle"
      icon={CreditCard}
      accent={PANEL_ACCENT.link}
      hasData={links.length > 0}
      count={links.length}
      defaultOpen
    >
      <p className="text-muted small">
        A vehicle can have <b>several</b> cards, and the same card can be shared by <b>several</b> vehicles. One of them
        is the vehicle&apos;s <b>default</b> - the card it normally uses. A purchase made with a card that is not in this
        list is recorded as a mismatched card.
      </p>
      {isLoading && <p className="text-muted small mb-2">Loading…</p>}
      {!isLoading && links.length === 0 && <p className="text-muted small mb-2">No cards assigned yet.</p>}
      {links.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-2">
            <thead>
              <tr>
                <th>Card</th>
                <th>Default</th>
                <th>Assigned</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.linkId}>
                  <td>
                    <span className="fw-semibold">{link.cardNumber}</span>
                    {!link.cardIsActive && <span className="badge badge-linedanger ms-2">Inactive</span>}
                    <div className="small text-muted">{link.provider}</div>
                  </td>
                  <td>
                    {canManage ? (
                      <div className="form-check form-switch mb-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={Boolean(link.isDefault)}
                          disabled={isLinking}
                          onChange={() => handleSetDefault(link)}
                        />
                        <label className="form-check-label small">{link.isDefault ? "Default" : "Not default"}</label>
                      </div>
                    ) : link.isDefault ? (
                      <span className="badge badge-linesuccess">Default</span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="small text-muted">
                    {formatDate(link.assignedDate)}
                    {link.assignedByName ? ` · ${link.assignedByName}` : ""}
                  </td>
                  {canManage && (
                    <td className="text-end">
                      <button type="button" className="btn btn-link p-1 text-danger" title="Remove" onClick={() => handleRemove(link)}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canManage && (
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <select className="form-control form-control-sm" style={{ width: 300 }} value={fuelCardId} onChange={(e) => setFuelCardId(e.target.value)}>
            <option value="">Assign a card ({vehicle.branchCode})…</option>
            {candidates.map((c) => (
              <option key={c.fuelCardId} value={c.fuelCardId}>
                {c.cardNumber} - {c.provider}{c.fuelType ? ` (${c.fuelType} only)` : ""} · bal {money(c.currentBalance)}
              </option>
            ))}
          </select>
          <div className="form-check mb-0">
            <input type="checkbox" className="form-check-input" id="fuelVehicleCardAsDefault" checked={asDefault} onChange={(e) => setAsDefault(e.target.checked)} />
            <label className="form-check-label small" htmlFor="fuelVehicleCardAsDefault">Make it the default card</label>
          </div>
          <button type="button" className="btn btn-sm btn-primary" disabled={!fuelCardId || isLinking} onClick={handleAdd}>
            {isLinking ? "Saving…" : "Assign card"}
          </button>
        </div>
      )}
    </CollapsibleCard>
  );
};

/**
 * One vehicle's fuel set-up: its fuel type and tank size, and the fuel cards it
 * may be fuelled with. The vehicle itself (reg no, allocation...) is edited in
 * the Vehicles master, not here.
 */
const VehicleFuelSetupModal = ({ vehicle, canManage, onClose }) => (
  <Modal show={Boolean(vehicle)} onHide={onClose} size="lg" centered>
    <Modal.Header closeButton>
      <Modal.Title>
        {vehicle?.regNo}
        <span className="text-muted fw-normal ms-2" style={{ fontSize: 14 }}>
          {vehicle?.brand} {vehicle?.model} · {vehicle?.branchCode}
        </span>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {vehicle && (
        <>
          <ProfilePanel vehicle={vehicle} canManage={canManage} />
          <CardsPanel vehicle={vehicle} canManage={canManage} />
        </>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

export default VehicleFuelSetupModal;
